import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicOrganisation } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateOrganisation } from "../api/update-organisation";
import {
  OrganisationAddressFormData,
  organisationAddressSchema,
} from "../types/organisation-address-schema";

type OrganisationAddressFormProps = {
  initialData: PublicOrganisation;
};

function apiDataToFormData(
  data: PublicOrganisation,
): OrganisationAddressFormData {
  return {
    streetAddress: data.streetAddress ?? "",
    town: data.town ?? "",
    postcode: data.postcode ?? "",
    region: data.region ?? "",
    country: data.country ?? "",
  };
}

export function OrganisationAddressForm(props: OrganisationAddressFormProps) {
  const { initialData } = props;
  const form = useForm<OrganisationAddressFormData>({
    resolver: zodResolver(organisationAddressSchema),
    defaultValues: apiDataToFormData(initialData),
  });
  const { mutateAsync: updateOrganisation } = useUpdateOrganisation({
    onSuccess: (result) => {
      form.reset(apiDataToFormData(result));
      queryClient.setQueryData(["organisation", result.id], result);
      queryClient.setQueryData(
        ["organisations"],
        (oldData: PublicOrganisation[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((org) => (org.id === result.id ? result : org));
        },
      );
    },
  });

  async function handleUpdateOrganisation(data: OrganisationAddressFormData) {
    try {
      await updateOrganisation({ id: initialData.id, organisation: data });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateOrganisation)}>
          <Card.Header title="Contact Details" />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="streetAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Street Address</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="123 Main St"
                      autoComplete="street-address"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="town"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel isRequired>Town/City</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="address-level2"
                        placeholder="London"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel isRequired>County</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="address-level1"
                        placeholder="Midlothian"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="postcode"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel isRequired>Postcode</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="postal-code"
                        placeholder="EH1 1AA"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel isRequired>Country</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="country-name"
                        placeholder="United Kingdom"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset(apiDataToFormData(initialData))}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
